import React from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { FiWifiOff as WifiOff, FiPhone as Phone } from 'react-icons/fi';
import { AGENCY_PHONE_DISPLAY, AGENCY_PHONE_TEL } from '../constants/contact';


const formatDate = (value) => {
  if (!value) return '—';
  const date = typeof value === 'string' ? parseISO(value) : new Date(value);
  return isValid(date) ? format(date, 'MM/dd/yyyy') : '—';
};

/**
 * A coverage card rendered from the client's own policy record, so it works
 * with no connection. This is a quick-reference card, not a substitute for
 * the carrier-issued proof of insurance — the disclaimer says so on the card.
 */
const DigitalIdCard = ({ policy, holderName }) => {
  const expired = policy.expirationDate
    ? new Date(policy.expirationDate) < new Date()
    : false;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-[#DC143C] to-[#B01030] text-white p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-bold leading-tight">PINOY GENERAL INSURANCE</h3>
            <p className="text-xs text-white/80 mt-0.5">Serving California since 1993</p>
          </div>
          <span className="shrink-0 inline-flex items-center gap-1 bg-white/15 rounded-full px-2.5 py-1 text-[11px] font-medium">
            <WifiOff size={12} /> Offline ready
          </span>
        </div>
        <div className="mt-4 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-white/70">Policyholder</div>
            <div className="font-semibold truncate">{holderName || '—'}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[11px] uppercase tracking-wide text-white/70">Type</div>
            <div className="font-semibold">{policy.policyType || '—'}</div>
          </div>
        </div>
      </div>

      <div className="p-5">
        <dl className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <dt className="text-xs text-gray-500 uppercase tracking-wide">Carrier</dt>
            <dd className="font-semibold text-gray-900 break-words">{policy.vendor || '—'}</dd>
          </div>
          <div className="col-span-2">
            <dt className="text-xs text-gray-500 uppercase tracking-wide">Policy Number</dt>
            <dd className="font-mono font-semibold text-gray-900 break-all">
              {policy.policyNumber || '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500 uppercase tracking-wide">Effective</dt>
            <dd className="font-semibold text-gray-900">{formatDate(policy.effectiveDate)}</dd>
          </div>
          <div>
            <dt className="text-xs text-gray-500 uppercase tracking-wide">Expires</dt>
            <dd className={`font-semibold ${expired ? 'text-[#DC143C]' : 'text-gray-900'}`}>
              {formatDate(policy.expirationDate)}
            </dd>
          </div>
        </dl>

        {expired && (
          <p className="mt-4 text-sm font-medium text-[#DC143C] bg-red-50 border border-red-200 rounded-lg p-3">
            This policy is past its expiration date. Call us to confirm your current coverage.
          </p>
        )}

        <a
          href={AGENCY_PHONE_TEL}
          className="mt-5 flex items-center justify-center gap-2 w-full bg-[#0038A8] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#002d8a] transition-colors"
        >
          <Phone size={18} /> Call your agent — {AGENCY_PHONE_DISPLAY}
        </a>

        <p className="mt-4 text-xs text-gray-500 leading-relaxed">
          Quick-reference summary of your coverage. Your carrier-issued ID card remains the
          official proof of insurance required by law.
        </p>
      </div>
    </div>
  );
};

export default DigitalIdCard;
